import { RULEBOOK_MIN_TAGS, RulebookVisibility, validateRulebook } from "@next-platform/packages-ts-types";
export const RULEBOOK_DRAFT_ISSUE_CODE = {
    MISSING_NAME: "missing_name",
    MISSING_DESCRIPTION: "missing_description",
    MISSING_TAGS: "missing_tags",
    TOO_FEW_TAGS: "too_few_tags",
    MISSING_COVER: "missing_cover",
    NO_METERS: "no_meters",
};
const isBlank = (text) => typeof text !== "string" || text.trim().length === 0;
/** `message` is a fallback; clients render these by `code` through i18n. */
const draftIssue = ({ code, path, message }) => ({
    severity: "error",
    code,
    path,
    message,
});
/** Zero tags block any save; a public draft needs at least RULEBOOK_MIN_TAGS distinct tags. */
const tagIssue = (meta) => {
    const tagCount = meta?.tags?.length ?? 0;
    const uniqueTagCount = new Set(meta?.tags ?? []).size;
    if (tagCount === 0) {
        return draftIssue({ code: RULEBOOK_DRAFT_ISSUE_CODE.MISSING_TAGS, path: "tags", message: "Pick at least one tag" });
    }
    if (meta?.visibility === RulebookVisibility.PUBLIC && uniqueTagCount < RULEBOOK_MIN_TAGS) {
        return draftIssue({ code: RULEBOOK_DRAFT_ISSUE_CODE.TOO_FEW_TAGS, path: "tags", message: "Pick at least two tags to publish" });
    }
    return null;
};
/**
 * Editor completeness errors: a Rulebook needs a name, description, at least one tag, a cover and at least one meter
 * before it can be saved; a public Rulebook additionally needs at least `RULEBOOK_MIN_TAGS` tags.
 * Cheap and synchronous so the Save gate never trusts a stale result.
 */
export function collectRulebookDraftCompletenessIssues({ definition, meta }) {
    const issues = [];
    if (isBlank(definition.name)) {
        issues.push(draftIssue({ code: RULEBOOK_DRAFT_ISSUE_CODE.MISSING_NAME, path: "name", message: "Give the rulebook a name" }));
    }
    if (isBlank(definition.description)) {
        issues.push(draftIssue({ code: RULEBOOK_DRAFT_ISSUE_CODE.MISSING_DESCRIPTION, path: "description", message: "Add a description" }));
    }
    const tags = tagIssue(meta);
    if (tags !== null) {
        issues.push(tags);
    }
    if (isBlank(meta?.cover_url)) {
        issues.push(draftIssue({ code: RULEBOOK_DRAFT_ISSUE_CODE.MISSING_COVER, path: "cover_url", message: "Set a cover image" }));
    }
    if (Array.isArray(definition.meters) && definition.meters.length === 0) {
        issues.push(draftIssue({ code: RULEBOOK_DRAFT_ISSUE_CODE.NO_METERS, path: "meters", message: "Add at least one meter" }));
    }
    return issues;
}
/** Prepends completeness errors to a structural result; `valid` is false while any is present. */
export function withRulebookDraftCompleteness({ structural, completeness, }) {
    if (completeness.length === 0) {
        return structural;
    }
    return { ...structural, valid: false, issues: [...completeness, ...structural.issues] };
}
/** Engine's structural `validateRulebook` plus editor completeness checks. */
export default function validateRulebookDraft(draft) {
    return withRulebookDraftCompleteness({
        structural: validateRulebook(draft.definition),
        completeness: collectRulebookDraftCompletenessIssues(draft),
    });
}
